/**
 * Os Tipos de Tarefa e os Resultados de cada um (FR-15, FR-16, FR-69) — desde a 11.2 na aba
 * Tarefas da Configuração.
 *
 * **O quarto cartão, e não a barra de abas.** O protótipo põe "Tipos de tarefa" numa aba, ao lado
 * de "Funis", de "Régua e listas" e de "Permissões". A 2.3 decidiu por escrito entregar cartões
 * do que existe em vez de abas vazias, e das quatro abas ainda falta uma: Permissões, da 1.6. Um
 * quarto cartão é a escolha consistente; a barra de abas entra quando a última aba tiver conteúdo.
 *
 * **Sem cabeçalho próprio.** O `Cabecalho` do shell já desenha o título ("Administração") e o
 * subtítulo do item de navegação; um `<h1>` aqui daria dois títulos na mesma tela.
 *
 * **Aqui o cartão respeita a permissão na tela — e é a segunda exceção, não a regra.** A régua
 * (3.6) foi a primeira, e o argumento é o mesmo: `gerir_tipos_de_tarefa` é configuração **global**,
 * que o FR-61-A concede a Admin e Gestor (emenda de 23/09/2026; só ligável em perfil de escopo
 * `todas`, 11.1). Mostrar seis botões de
 * escrita a um Vendedor que os verá recusados a cada clique não é "esconder não é autorizar" — é
 * oferecer um caminho que não existe para ele. Quem recusa continua sendo o serviço, e a mensagem
 * dele vai à tela verbatim quando alguém chegar lá por outro caminho; o gate só evita a ida à
 * rede de um pedido que já se sabe recusado. A **leitura** continua para todo mundo, como no
 * serviço.
 *
 * **Excluir é em dois passos, e o que ele faz é diferente do dos Funis.** "Excluir" abre
 * "Confirmar exclusão"/"Cancelar" na própria linha. Aqui a exclusão é **lógica** (FR-69): o Tipo
 * some da criação de Tarefas novas e as Tarefas antigas continuam exibindo o que já estava
 * gravado. Quem está na tela não vê isso — para ele, o Tipo sumiu —, e é por isso que a
 * confirmação diz o que sobrevive: sem ela, "excluir" parece apagar histórico.
 *
 * **Não há "Bloqueado" aqui**, ao contrário do cartão de Funis. Lá o FR-68 recusa a exclusão
 * enquanto houver Negócio na Etapa, e a tela antecipa a recusa; aqui não há recusa a antecipar —
 * excluir um Tipo em uso é justamente o caso que o FR-69 descreve, e ele é sempre aceito.
 *
 * **Os Resultados ficam aninhados no Tipo, e não numa tela própria.** É assim que o serviço os
 * entrega (`GET /tipos-de-tarefa` traz os dois juntos) e é assim que o FR-17 os usa: "as opções
 * oferecidas são só as do Tipo daquela Tarefa". Uma lista solta de Resultados perderia o dono, que
 * é a única coisa que os distingue — "Realizada" existe em Reunião **e** em Visita.
 *
 * **Depois de cada mutação o catálogo é relido do serviço**, e não remendado com a resposta: a
 * resposta de uma mutação é um Tipo (ou um Resultado), e o que a tela desenha é a lista inteira.
 *
 * **Paridade de celular (NFR-1).** Não há função exclusiva do desktop: o que muda é a grade —
 * dois Tipos lado a lado no computador, empilhados no celular —, e a decisão é do `useEhCelular`
 * do shell, por `matchMedia`. Sem `@media`.
 */

import { type FormEvent, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { useEhCelular } from '@/shell/Shell'
import { ICONE_DO_WHATSAPP, IconeDoTipo, nomeComIcone, rotuloDoIcone } from './IconeDoTipo'
import {
  type ContaComo,
  type ResultadoDeTarefa,
  type TipoDeTarefa,
  useCadastrarResultado,
  useConfigurarResultado,
  useConfigurarSugestao,
  useConfigurarTipoDeTarefa,
  useCadastrarTipoDeTarefa,
  useExcluirTipoDeTarefa,
  useRemoverResultado,
  useRenomearResultado,
  useRenomearTipoDeTarefa,
  useTiposDeTarefa,
} from './api'
import './tipos-de-tarefa.css'

/**
 * A permissão do FR-61-A que este cartão respeita, escrita uma vez.
 *
 * Ela é comparada contra a claim do JWT, que só carrega as **não negadas** — ausência é negação
 * (AD-5). Não há distinção entre `concedida` e `limitada` aqui, e não pode haver: é o mesmo
 * `exigir_permissao` do serviço, que testa pertencimento, e duas leituras diferentes da mesma
 * claim fariam a tela e o serviço discordarem sobre quem pode.
 */
const PERMISSAO_DE_GERIR = 'gerir_tipos_de_tarefa'

/** A permissão da sugestão por Resultado (11.6, FR-21) — outra porta, teto escopo `todas`. */
const PERMISSAO_DA_SUGESTAO = 'configurar_sugestao_por_resultado'

/**
 * A paleta do protótipo (`p1-dados.js`): emoji, sempre ao lado do nome — nunca sozinho. O último
 * é o token do **logotipo do WhatsApp** (28/09/2026), que `IconeDoTipo` desenha em SVG.
 */
export const PALETA_DE_ICONES = [
  '📞',
  '🤝',
  '✉️',
  '💬',
  '📄',
  '🚗',
  '📅',
  '⭐',
  ICONE_DO_WHATSAPP,
] as const

export const SEM_ICONE = 'Sem ícone'

/** O marcador do Tipo: é por ele — e não pelo nome — que Ranking e relatórios contam. */
export const ROTULOS_DO_CONTA_COMO: Record<ContaComo | '', string> = {
  '': 'Não conta',
  reuniao: 'Reunião',
  ligacao: 'Ligação',
}

export const CONTA_COMO_EXITO = 'Conta como êxito'
/**
 * A regra padrão, sem o número de dias: ele é do serviço (`DIAS_DA_SUGESTAO_PADRAO`) e não viaja no
 * catálogo — escrevê-lo aqui seria uma segunda cópia a divergir. A frase da conclusão, essa sim,
 * diz os dias, porque eles chegam na resposta (`prazo_em_dias`).
 */
export const SUGESTAO_PADRAO = 'Padrão: mesmo tipo'
export const SALVAR_SUGESTAO = 'Salvar sugestão'
export const CONFIGURACAO_SALVA = 'Configuração salva.'
export const SUGESTAO_SALVA = 'Sugestão salva. Vale na próxima conclusão.'

/** A frase da sugestão configurada, para quem só lê. */
function descreverASugestao(resultado: ResultadoDeTarefa, tipos: TipoDeTarefa[]): string {
  if (resultado.tipo_sugerido_id === null || resultado.prazo_sugerido_em_dias === null) {
    return SUGESTAO_PADRAO
  }
  const tipo = tipos.find((cada) => cada.id === resultado.tipo_sugerido_id)
  const dias = resultado.prazo_sugerido_em_dias
  return `Sugere ${tipo?.nome ?? 'tipo excluído'} em ${dias} ${dias === 1 ? 'dia' : 'dias'}`
}

/**
 * O ícone e o marcador do Tipo (11.6). Gravam na troca — a escolha é o gesto, e não há rascunho
 * para perder. O corpo é completo: o outro campo vai como está.
 */
function ConfiguracaoDoTipo({
  tipo,
  ocupado,
  aoGravar,
}: {
  tipo: TipoDeTarefa
  ocupado: boolean
  aoGravar: (icone: string | null, contaComo: ContaComo | null) => void
}) {
  const idDoIcone = `tipo-icone-${tipo.id}`
  const idDoMarcador = `tipo-conta-como-${tipo.id}`
  const icone = tipo.icone ?? ''
  const contaComo = tipo.conta_como ?? ''
  const paleta: readonly string[] = PALETA_DE_ICONES
  return (
    <div className="tipos__configuracao">
      <label className="tipos__rotulo" htmlFor={idDoIcone}>
        Ícone de {tipo.nome}
      </label>
      <select
        id={idDoIcone}
        className="tipos__entrada tipos__entrada--curta"
        value={icone}
        disabled={ocupado}
        onChange={(evento) => {
          aoGravar(evento.target.value === '' ? null : evento.target.value, tipo.conta_como ?? null)
        }}
      >
        <option value="">{SEM_ICONE}</option>
        {icone !== '' && !paleta.includes(icone) ? <option value={icone}>{icone}</option> : null}
        {paleta.map((cada) => (
          <option key={cada} value={cada}>
            {rotuloDoIcone(cada)}
          </option>
        ))}
      </select>
      <label className="tipos__rotulo" htmlFor={idDoMarcador}>
        {tipo.nome} conta como
      </label>
      <select
        id={idDoMarcador}
        className="tipos__entrada tipos__entrada--curta"
        value={contaComo}
        disabled={ocupado}
        onChange={(evento) => {
          const valor = evento.target.value
          aoGravar(tipo.icone ?? null, valor === '' ? null : (valor as ContaComo))
        }}
      >
        {(Object.keys(ROTULOS_DO_CONTA_COMO) as (ContaComo | '')[]).map((cada) => (
          <option key={cada} value={cada}>
            {ROTULOS_DO_CONTA_COMO[cada]}
          </option>
        ))}
      </select>
    </div>
  )
}

/**
 * O editor da sugestão por Resultado (FR-21): Tipo + dias, ou o padrão (os dois nulos). O serviço
 * recusa com `422` o par pela metade, dias < 1 e Tipo inexistente — a mensagem chega verbatim.
 */
function EditorDaSugestao({
  tipo,
  resultado,
  tipos,
  ocupado,
  aoGravar,
}: {
  tipo: TipoDeTarefa
  resultado: ResultadoDeTarefa
  tipos: TipoDeTarefa[]
  ocupado: boolean
  aoGravar: (tipoSugeridoId: string | null, dias: number | null) => void
}) {
  const [tipoSugerido, setTipoSugerido] = useState(resultado.tipo_sugerido_id ?? '')
  const [dias, setDias] = useState(
    resultado.prazo_sugerido_em_dias === null ? '' : String(resultado.prazo_sugerido_em_dias),
  )
  const idDoTipo = `sugestao-tipo-${resultado.id}`
  const idDosDias = `sugestao-dias-${resultado.id}`
  return (
    <form
      className="tipos__configuracao"
      noValidate
      onSubmit={(evento) => {
        evento.preventDefault()
        if (tipoSugerido === '') {
          aoGravar(null, null)
          return
        }
        aoGravar(tipoSugerido, dias === '' ? null : Number(dias))
      }}
    >
      <label className="tipos__rotulo" htmlFor={idDoTipo}>
        Sugestão após {resultado.nome} em {tipo.nome}
      </label>
      <select
        id={idDoTipo}
        className="tipos__entrada tipos__entrada--curta"
        value={tipoSugerido}
        disabled={ocupado}
        onChange={(evento) => {
          setTipoSugerido(evento.target.value)
          if (evento.target.value !== '' && dias === '') {
            setDias('1')
          }
        }}
      >
        <option value="">{SUGESTAO_PADRAO}</option>
        {tipos.map((cada) => (
          <option key={cada.id} value={cada.id}>
            {nomeComIcone(cada)}
          </option>
        ))}
      </select>
      <label className="tipos__rotulo" htmlFor={idDosDias}>
        Dias
      </label>
      <input
        id={idDosDias}
        className="tipos__entrada tipos__entrada--curta"
        type="number"
        min={1}
        max={365}
        value={dias}
        disabled={ocupado || tipoSugerido === ''}
        onChange={(evento) => {
          setDias(evento.target.value)
        }}
      />
      <button
        className="tipos__botao tipos__botao--discreto"
        type="submit"
        disabled={ocupado}
        aria-label={`${SALVAR_SUGESTAO} de ${resultado.nome} em ${tipo.nome}`}
      >
        {SALVAR_SUGESTAO}
      </button>
    </form>
  )
}

/**
 * O teto de coluna do serviço (`servicos/negocios/dominio/tipo_de_tarefa.py`), pela mesma regra do
 * formulário da ficha: o servidor continua sendo quem decide, e o `maxLength` só evita gastar uma
 * ida à rede com o que o cliente já sabe.
 *
 * Ele é **60**, e não os 120 do nome de Funil: o rótulo cabe num seletor de Tarefa ao lado do
 * prazo e do Responsável.
 */
const TAMANHO_DO_NOME = 60

const FALTA_NOME = 'Informe o nome.'

/** Confirmações. Sem elas a pessoa reenvia por não saber se gravou. */
const TIPO_CRIADO = 'Tipo de tarefa criado.'

/**
 * Restaurar não é criar, e a confirmação diz qual dos dois aconteceu.
 *
 * Quando o nome já existia **excluído**, o serviço reativa a linha antiga: o `id` é o mesmo, e
 * portanto toda Tarefa que já apontava para ela volta a apontar para um Tipo vivo — com os
 * Resultados que ele tinha. Dizer "Tipo de tarefa criado." ali esconderia justamente o que a
 * pessoa precisa saber: que ela não criou um Tipo novo, e sim trouxe de volta um que estava em
 * uso — e que os Resultados voltaram junto.
 */
const TIPO_RESTAURADO =
  'Tipo de tarefa restaurado — ele já existia e estava excluído, e os resultados dele voltaram.'

const TIPO_SALVO = 'Tipo de tarefa salvo.'

/**
 * A confirmação da exclusão diz o que **sobrevive**, e não só que o ato aconteceu.
 *
 * É a diferença que a tela não mostra: para quem está olhando, o Tipo sumiu. Sem esta frase,
 * "Tipo de tarefa excluído." leria como "apaguei o histórico" — e o FR-69 promete o contrário.
 */
const TIPO_EXCLUIDO =
  'Tipo de tarefa excluído. As tarefas antigas continuam exibindo o tipo com que foram criadas.'

const RESULTADO_CRIADO = 'Resultado adicionado.'
const RESULTADO_RESTAURADO = 'Resultado restaurado — ele já existia neste tipo e estava removido.'
const RESULTADO_SALVO = 'Resultado salvo.'
const RESULTADO_REMOVIDO =
  'Resultado removido. As tarefas concluídas continuam exibindo o resultado gravado nelas.'

const CARREGANDO = 'Carregando os tipos de tarefa…'
/**
 * O catálogo vazio tem **dois** textos, e não um.
 *
 * "Crie o primeiro abaixo" só é verdade para quem tem o formulário abaixo. Para quem não
 * administra — o Gestor comercial, o Vendedor, a SDR — a frase manda fazer uma coisa que a tela
 * não oferece, e a pessoa procuraria um campo que não existe. O segundo texto diz o que de fato
 * acontece: não há tipos, e quem os cria é outra pessoa.
 */
const SEM_TIPOS = 'Nenhum tipo de tarefa cadastrado. Crie o primeiro abaixo.'

const SEM_TIPOS_PARA_QUEM_NAO_GERE =
  'Nenhum tipo de tarefa cadastrado. Só o Admin pode cadastrar o primeiro.'
const SEM_RESULTADOS = 'Este tipo ainda não tem resultados.'

/** O que quem não administra lê no lugar das ações. */
const SOMENTE_LEITURA =
  'Só o Admin cria, renomeia e exclui tipos de tarefa e resultados. Você pode consultá-los.'

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'tipos-de-tarefa-titulo'

/** `id` do campo do formulário de novo tipo — a recusa local dele deriva deste. */
const ID_DO_CAMPO_DO_NOVO_TIPO = 'tipo-de-tarefa-nome-novo'

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** "1 resultado" / "n resultados" / "sem resultados" — o subtítulo de cada Tipo. */
function contagem(quantidade: number): string {
  if (quantidade === 0) {
    return 'sem resultados'
  }
  return quantidade === 1 ? '1 resultado' : `${String(quantidade)} resultados`
}

function CampoDeNome({
  id,
  rotulo,
  valor,
  aoTrocar,
  recusadoPor,
}: {
  id: string
  rotulo: string
  valor: string
  aoTrocar: (valor: string) => void
  /**
   * `id` da mensagem que recusou **este** campo, quando há uma.
   *
   * É o que liga a recusa ao campo que ela nomeia: sem `aria-invalid` e `aria-describedby`, quem
   * usa leitor de tela ouve "Informe o nome" solto e precisa adivinhar de qual dos formulários da
   * tela a mensagem fala.
   */
  recusadoPor?: string | null
}) {
  return (
    <div className="tipos__campo">
      <label className="tipos__rotulo" htmlFor={id}>
        {rotulo}
      </label>
      <input
        id={id}
        className="tipos__entrada"
        type="text"
        value={valor}
        maxLength={TAMANHO_DO_NOME}
        aria-invalid={recusadoPor ? true : undefined}
        aria-describedby={recusadoPor ?? undefined}
        onChange={(evento) => {
          aoTrocar(evento.target.value)
        }}
      />
    </div>
  )
}

/**
 * O par de ações de exclusão de uma linha: dois passos, sempre.
 *
 * Ele é um componente porque a regra é a **mesma** para o Tipo e para o Resultado — os dois somem
 * logicamente, e nenhum dos dois tem recusa a antecipar —, e duas cópias divergiriam no primeiro
 * ajuste.
 */
function AcaoDeExcluir({
  alvo,
  acao,
  aConfirmar,
  ocupado,
  aoPedir,
  aoCancelar,
  aoExcluir,
}: {
  /**
   * O que some, por extenso — vai no `aria-label`, porque os botões são muitos e iguais.
   *
   * Ele entra depois de "a exclusão d…" / "a remoção d…", e não colado no verbo: montado como
   * `${acao.confirmar} ${alvo}`, o rótulo sairia "Confirmar exclusão o tipo Visita", e o do
   * cancelar sairia "Cancelar o tipo Visita" — que numa tela onde existe um botão "Excluir" ao
   * lado lê-se como *cancelar o tipo*, e não como desistir da confirmação. O valor chega com o
   * artigo ("o tipo Visita") e a preposição é contraída aqui.
   */
  alvo: string
  /**
   * O verbo desta linha: "Excluir" no Tipo, "Remover" no Resultado.
   *
   * São dois porque o requisito usa dois: o FR-16 fala em **excluir** Tipos e em **remover**
   * opções de Resultado. Uma palavra só na tela obrigaria quem lê o requisito a traduzir.
   */
  acao: {
    pedir: string
    confirmar: string
    /** O ato, por extenso, para os `aria-label`: "a exclusão", "a remoção". */
    substantivo: string
  }
  aConfirmar: boolean
  ocupado: boolean
  aoPedir: () => void
  aoCancelar: () => void
  aoExcluir: () => void
}) {
  if (aConfirmar) {
    return (
      <>
        {/*
         * O segundo passo. O nome do alvo vai no `aria-label` porque numa tela com vários tipos e
         * vários resultados um "Confirmar exclusão" solto não diz **qual** some.
         */}
        <button
          className="tipos__botao tipos__botao--perigo"
          type="button"
          disabled={ocupado}
          aria-label={`Confirmar ${acao.substantivo} d${alvo}`}
          onClick={aoExcluir}
        >
          {acao.confirmar}
        </button>
        <button
          className="tipos__botao tipos__botao--discreto"
          type="button"
          disabled={ocupado}
          aria-label={`Cancelar ${acao.substantivo} d${alvo}`}
          onClick={aoCancelar}
        >
          Cancelar
        </button>
      </>
    )
  }

  return (
    <button
      className="tipos__botao tipos__botao--discreto"
      type="button"
      disabled={ocupado}
      aria-label={`${acao.pedir} ${alvo}`}
      onClick={aoPedir}
    >
      {acao.pedir}
    </button>
  )
}

/** Um formulário de um campo só — o de renomear em linha e o de criar. */
function FormularioDeNome({
  id,
  rotulo,
  inicial,
  acao,
  cancelar,
  ocupado,
  enviando,
  aoEnviar,
  aoCancelar,
}: {
  id: string
  rotulo: string
  inicial: string
  /** O texto do botão de envio, e o que ele diz enquanto grava. */
  acao: { parado: string; gravando: string; nomeAcessivel: string }
  /** O rótulo acessível do "Cancelar", quando este formulário tem um. */
  cancelar?: string
  ocupado: boolean
  enviando: boolean
  aoEnviar: (nome: string) => void
  aoCancelar?: () => void
}) {
  const [rascunho, setRascunho] = useState(inicial)
  const [recusa, setRecusa] = useState<string | null>(null)
  const idDaRecusa = `${id}-recusa`

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (rascunho.trim() === '') {
      setRecusa(FALTA_NOME)
      return
    }
    aoEnviar(rascunho.trim())
  }

  return (
    <form className="tipos__formulario" onSubmit={enviar} noValidate>
      <CampoDeNome
        id={id}
        rotulo={rotulo}
        valor={rascunho}
        recusadoPor={recusa === null ? null : idDaRecusa}
        aoTrocar={(valor) => {
          setRecusa(null)
          setRascunho(valor)
        }}
      />
      {recusa === null ? null : (
        <p className="tipos__erro" id={idDaRecusa} role="alert">
          {recusa}
        </p>
      )}
      <div className="tipos__acoes">
        {/* O rótulo segue **este** envio, e o `disabled` segue a tela inteira: outra gravação
            ocupa a tela, mas não é esta que está gravando — e um botão que diz "Salvando…" sem
            estar salvando mente para o leitor de tela. */}
        <button
          className="tipos__botao"
          type="submit"
          disabled={ocupado}
          aria-label={acao.nomeAcessivel}
        >
          {enviando ? acao.gravando : acao.parado}
        </button>
        {aoCancelar === undefined ? null : (
          <button
            className="tipos__botao tipos__botao--discreto"
            type="button"
            disabled={ocupado}
            aria-label={cancelar}
            onClick={() => {
              setRascunho(inicial)
              setRecusa(null)
              aoCancelar()
            }}
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  )
}

export function TiposDeTarefa() {
  const ehCelular = useEhCelular()
  const { data: sessao } = useSessao()
  const consulta = useTiposDeTarefa()
  const criacaoDeTipo = useCadastrarTipoDeTarefa()
  const renomeacaoDeTipo = useRenomearTipoDeTarefa()
  const exclusaoDeTipo = useExcluirTipoDeTarefa()
  const criacaoDeResultado = useCadastrarResultado()
  const renomeacaoDeResultado = useRenomearResultado()
  const remocaoDeResultado = useRemoverResultado()
  const configuracaoDoTipo = useConfigurarTipoDeTarefa()
  const configuracaoDoResultado = useConfigurarResultado()
  const configuracaoDaSugestao = useConfigurarSugestao()

  /**
   * Quem pode administrar, pela claim do JWT.
   *
   * `in`, e não uma comparação com `'concedida'`: a claim só carrega as permissões **não negadas**
   * (AD-5), e `limitada` passa pela porta tanto quanto `concedida` — é o que a guarda do serviço
   * faz, e duas leituras diferentes da mesma claim fariam a tela e o serviço discordarem.
   *
   * Sem sessão o cartão fica somente-leitura, e não em branco: a guarda de rota não deixa nada
   * interno renderizar sem sessão, e este ramo é a defesa contra o estado que ela já impede.
   */
  const podeGerir = sessao !== undefined && PERMISSAO_DE_GERIR in sessao.permissoes
  const podeSugerir = sessao !== undefined && PERMISSAO_DA_SUGESTAO in sessao.permissoes

  /** O `id` da linha aberta em edição — Tipo ou Resultado, uma por vez na tela inteira. */
  const [emEdicaoPedido, setEmEdicao] = useState<string | null>(null)
  /** O `id` do que pediu exclusão e espera a confirmação. */
  const [aConfirmarPedido, setAConfirmar] = useState<string | null>(null)
  /** O `id` do Tipo cujo formulário de "Novo resultado" está aberto. */
  const [comResultadoNovoPedido, setComResultadoNovo] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [confirmacao, setConfirmacao] = useState<string | null>(null)
  /**
   * Quantos tipos esta montagem já criou — é a `key` do formulário de "Novo tipo".
   *
   * O rascunho mora **dentro** do formulário (é ele quem valida o campo vazio), e por isso
   * limpá-lo daqui é remontá-lo. Sem isto, o nome recém-criado continuaria no campo e o clique
   * seguinte mandaria o mesmo nome de novo — que o serviço recusa com `409`, mandando quem usa
   * procurar o erro num campo que ele não digitou.
   */
  const [tiposCriados, setTiposCriados] = useState(0)

  const ocupado =
    criacaoDeTipo.isPending ||
    renomeacaoDeTipo.isPending ||
    exclusaoDeTipo.isPending ||
    criacaoDeResultado.isPending ||
    renomeacaoDeResultado.isPending ||
    remocaoDeResultado.isPending ||
    configuracaoDoTipo.isPending ||
    configuracaoDoResultado.isPending ||
    configuracaoDaSugestao.isPending

  const tipos = consulta.data

  /**
   * Os `id` que a releitura ainda traz — os dos Tipos **e** os dos Resultados deles.
   *
   * Ele existe para uma coisa só: um ponteiro de formulário aberto pode apontar para uma linha
   * que a releitura seguinte não traz mais. Excluir um Tipo com o "+ Novo resultado" dele aberto é
   * o caminho curto para isso, e o estado que sobra é o pior tipo de estado travado — nenhum
   * artigo renderiza aquele formulário, não há "Cancelar" para clicar, e `formularioAberto` fica
   * verdadeiro para sempre, deixando todo "Renomear" e todo "+ Novo resultado" do cartão
   * desabilitados até alguém remontar a tela.
   *
   * A limpeza é **derivada**, e não um `useEffect`: um efeito renderiza um quadro inteiro com o
   * ponteiro morto antes de corrigi-lo, e precisaria depender da identidade do array — que muda a
   * cada releitura, inclusive quando nada mudou. Derivando, o ponteiro morto nunca chega a
   * governar um render.
   */
  const idsVivos = new Set(
    (tipos ?? []).flatMap((tipo) => [tipo.id, ...tipo.resultados.map((linha) => linha.id)]),
  )

  /**
   * O ponteiro de edição **depois** da limpeza, e o de "novo resultado" ao lado dele.
   *
   * Enquanto o catálogo não chegou (`tipos === undefined`) nada é limpo: ali não se sabe que
   * linhas existem, e apagar o ponteiro seria jogar fora um rascunho por desconhecimento — o
   * mesmo erro que o "Carregando…" existe para não cometer.
   */
  const desconhecido = tipos === undefined
  const emEdicao =
    emEdicaoPedido !== null && (desconhecido || idsVivos.has(emEdicaoPedido))
      ? emEdicaoPedido
      : null
  const comResultadoNovo =
    comResultadoNovoPedido !== null && (desconhecido || idsVivos.has(comResultadoNovoPedido))
      ? comResultadoNovoPedido
      : null
  /**
   * E o ponteiro da confirmação passa pela **mesma** limpeza, pela mesma razão.
   *
   * Hoje ele não trava nada — a linha morta simplesmente não é desenhada, e `aConfirmar === id`
   * nunca casa —, mas a linha pode **voltar com o mesmo `id`**: recadastrar o nome de um Tipo
   * excluído reativa aquela linha, e não cria outra. Sem a limpeza, quem pediu "Excluir", viu
   * outro Admin excluir antes e depois trouxe o Tipo de volta reencontraria a linha já aberta em
   * "Confirmar exclusão", a um clique de apagar o que acabou de restaurar. Deixá-lo de fora
   * também seria a assimetria que regride no dia em que ele passar a governar as outras ações,
   * como `emEdicao` governa.
   */
  const aConfirmar =
    aConfirmarPedido !== null && (desconhecido || idsVivos.has(aConfirmarPedido))
      ? aConfirmarPedido
      : null

  /**
   * Há rascunho em algum formulário da tela — de edição ou de resultado novo.
   *
   * **É a única guarda de "não jogue fora o que já foi digitado", e ela governa as quatro ações
   * de cada linha.** Antes havia duas — esta, nos botões que *abrem* formulário, e um
   * `emEdicao !== null` nas de excluir — e as duas discordavam: com o "+ Novo resultado" aberto,
   * "Renomear" ficava travado e "Excluir" não, e excluir dali levava embora o rascunho **e** a
   * linha que o formulário pendurava. O princípio é um só, e agora a regra também.
   *
   * Ela é escrita sobre os ponteiros **limpos**: um formulário que não está mais na tela não
   * segura nada.
   */
  const formularioAberto = emEdicao !== null || comResultadoNovo !== null

  /**
   * Os avisos falam do envio que está em voo — nunca do anterior.
   *
   * Sem isto, a recusa de uma gravação continua na tela enquanto a próxima corre, e como o
   * `role="status"` da confirmação só aparece com `erro === null`, o sucesso seguinte ficaria mudo
   * até que a mudança de estado o limpasse.
   */
  function limparOsAvisos() {
    setErro(null)
    setConfirmacao(null)
  }

  function aoFalhar(falha: unknown) {
    setConfirmacao(null)
    setErro(mensagemDe(falha))
  }

  /** O desfecho comum das seis mutações: confirma, fecha o que estava aberto, ou explica. */
  function aoTerminar(mensagem: string, fechar?: () => void) {
    return {
      onSuccess: () => {
        setErro(null)
        setConfirmacao(mensagem)
        fechar?.()
      },
      onError: aoFalhar,
    }
  }

  function configurarOTipo(tipoId: string, icone: string | null, contaComo: ContaComo | null) {
    limparOsAvisos()
    configuracaoDoTipo.mutate(
      { tipoId, configuracao: { icone, conta_como: contaComo } },
      aoTerminar(CONFIGURACAO_SALVA),
    )
  }

  function marcarOExito(tipoId: string, resultadoId: string, contaComoExito: boolean) {
    limparOsAvisos()
    configuracaoDoResultado.mutate(
      { tipoId, resultadoId, contaComoExito },
      aoTerminar(CONFIGURACAO_SALVA),
    )
  }

  function configurarASugestao(
    tipoId: string,
    resultadoId: string,
    tipoSugeridoId: string | null,
    dias: number | null,
  ) {
    limparOsAvisos()
    configuracaoDaSugestao.mutate(
      {
        tipoId,
        resultadoId,
        sugestao: { tipo_sugerido_id: tipoSugeridoId, prazo_em_dias: dias },
      },
      aoTerminar(SUGESTAO_SALVA),
    )
  }

  function criarOTipo(nome: string) {
    limparOsAvisos()
    criacaoDeTipo.mutate(nome, {
      onSuccess: (cadastrado) => {
        setErro(null)
        // A confirmação depende do **status**, e não do corpo: os dois desfechos devolvem o mesmo
        // Tipo, e só o `200` distingue "restaurei" de "criei".
        setConfirmacao(cadastrado.restaurado ? TIPO_RESTAURADO : TIPO_CRIADO)
        setTiposCriados((quantos) => quantos + 1)
      },
      onError: aoFalhar,
    })
  }

  function renomearOTipo(tipoId: string, nome: string) {
    limparOsAvisos()
    renomeacaoDeTipo.mutate(
      { tipoId, nome },
      aoTerminar(TIPO_SALVO, () => {
        setEmEdicao(null)
      }),
    )
  }

  function excluirOTipo(tipoId: string) {
    limparOsAvisos()
    exclusaoDeTipo.mutate(
      tipoId,
      aoTerminar(TIPO_EXCLUIDO, () => {
        setAConfirmar(null)
      }),
    )
  }

  function criarOResultado(tipoId: string, nome: string) {
    limparOsAvisos()
    criacaoDeResultado.mutate(
      { tipoId, nome },
      {
        onSuccess: (cadastrado) => {
          setErro(null)
          setConfirmacao(cadastrado.restaurado ? RESULTADO_RESTAURADO : RESULTADO_CRIADO)
          setComResultadoNovo(null)
        },
        onError: aoFalhar,
      },
    )
  }

  function renomearOResultado(tipoId: string, resultadoId: string, nome: string) {
    limparOsAvisos()
    renomeacaoDeResultado.mutate(
      { tipoId, resultadoId, nome },
      aoTerminar(RESULTADO_SALVO, () => {
        setEmEdicao(null)
      }),
    )
  }

  function removerOResultado(tipoId: string, resultadoId: string) {
    limparOsAvisos()
    remocaoDeResultado.mutate(
      { tipoId, resultadoId },
      aoTerminar(RESULTADO_REMOVIDO, () => {
        setAConfirmar(null)
      }),
    )
  }

  return (
    <section className="tipos" aria-labelledby={ID_DO_TITULO}>
      <div className="tipos__cartao">
        <div className="tipos__cabecalho">
          <h2 className="tipos__titulo" id={ID_DO_TITULO}>
            Tipos de tarefa
          </h2>
          <span className="tipos__ajuda">
            Cada tipo de tarefa tem os resultados dele. Excluir um tipo ou remover um resultado tira
            a opção das tarefas novas — as tarefas antigas continuam exibindo o que já foi gravado.
          </span>
        </div>

        <div className="tipos__corpo">
          {consulta.isError ? (
            /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
            <p className="tipos__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : /* `|| tipos === undefined` é o que fecha o terceiro estado: sem rede, a consulta
                fica **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela diria
                "nenhum tipo cadastrado", afirmando ausência onde só há desconhecimento. */
          consulta.isLoading || tipos === undefined ? (
            <p className="tipos__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : tipos.length === 0 ? (
            <p className="tipos__ajuda">{podeGerir ? SEM_TIPOS : SEM_TIPOS_PARA_QUEM_NAO_GERE}</p>
          ) : (
            /* A grade: dois no computador, empilhados no celular. A decisão é do `useEhCelular`
               do shell, e não de uma `@media` — é o mesmo mecanismo que decide a barra inferior, e
               ter duas fontes de verdade de "isto é um celular" faria a tela e o shell
               discordarem numa largura de fronteira. */
            <div className={`tipos__grade${ehCelular ? ' tipos__grade--empilhada' : ''}`}>
              {tipos.map((tipo) => (
                <article className="tipos__tipo" key={`${tipo.id}-${tipo.nome}`}>
                  {emEdicao === tipo.id ? (
                    <FormularioDeNome
                      id={`tipo-de-tarefa-nome-${tipo.id}`}
                      rotulo="Novo nome do tipo"
                      inicial={tipo.nome}
                      acao={{
                        parado: 'Salvar tipo',
                        gravando: 'Salvando…',
                        nomeAcessivel: `Salvar o tipo ${tipo.nome}`,
                      }}
                      cancelar={`Cancelar a edição de ${tipo.nome}`}
                      ocupado={ocupado}
                      enviando={renomeacaoDeTipo.isPending}
                      aoEnviar={(nome) => {
                        renomearOTipo(tipo.id, nome)
                      }}
                      aoCancelar={() => {
                        limparOsAvisos()
                        setEmEdicao(null)
                      }}
                    />
                  ) : (
                    <div className="tipos__linha tipos__linha--tipo">
                      <h3 className="tipos__nome-do-tipo">
                        {tipo.icone ? (
                          <span className="tipos__icone" aria-hidden="true">
                            <IconeDoTipo icone={tipo.icone} />
                          </span>
                        ) : null}
                        {tipo.nome}
                      </h3>
                      <span className="tipos__status">{contagem(tipo.resultados.length)}</span>
                      {podeGerir ? (
                        <div className="tipos__acoes">
                          <button
                            className="tipos__botao tipos__botao--discreto"
                            type="button"
                            disabled={ocupado || formularioAberto}
                            aria-label={`Renomear o tipo ${tipo.nome}`}
                            onClick={() => {
                              limparOsAvisos()
                              setAConfirmar(null)
                              setEmEdicao(tipo.id)
                            }}
                          >
                            Renomear
                          </button>
                          <AcaoDeExcluir
                            alvo={`o tipo ${tipo.nome}`}
                            acao={{
                              pedir: 'Excluir',
                              confirmar: 'Confirmar exclusão',
                              substantivo: 'a exclusão',
                            }}
                            aConfirmar={aConfirmar === tipo.id}
                            ocupado={ocupado || formularioAberto}
                            aoPedir={() => {
                              limparOsAvisos()
                              setAConfirmar(tipo.id)
                            }}
                            aoCancelar={() => {
                              limparOsAvisos()
                              setAConfirmar(null)
                            }}
                            aoExcluir={() => {
                              excluirOTipo(tipo.id)
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  )}

                  {podeGerir ? (
                    <ConfiguracaoDoTipo
                      tipo={tipo}
                      ocupado={ocupado}
                      aoGravar={(icone, contaComo) => {
                        configurarOTipo(tipo.id, icone, contaComo)
                      }}
                    />
                  ) : tipo.conta_como ? (
                    <p className="tipos__ajuda">
                      Conta como {ROTULOS_DO_CONTA_COMO[tipo.conta_como]}
                    </p>
                  ) : null}

                  {tipo.resultados.length === 0 ? (
                    <p className="tipos__ajuda">{SEM_RESULTADOS}</p>
                  ) : (
                    /* A `<ul>` ganha nome próprio: são várias listas na tela, e sem ele o leitor
                       de tela anuncia "lista" e quem navega por marcos não sabe em qual entrou. */
                    <ul className="tipos__resultados" aria-label={`Resultados de ${tipo.nome}`}>
                      {tipo.resultados.map((resultado) => (
                        <li
                          className="tipos__item"
                          // `key` com o nome junto: a linha guarda o rascunho da edição no estado,
                          // e sem a remontagem ele atravessaria uma releitura da lista — reabrir
                          // "Renomear" mostraria o texto velho, e salvar desfaria a alteração de
                          // outra pessoa. Mesma regra da 2.3 e da 3.1.
                          key={`${resultado.id}-${resultado.nome}`}
                        >
                          {emEdicao === resultado.id ? (
                            <FormularioDeNome
                              id={`resultado-nome-${resultado.id}`}
                              rotulo="Novo nome do resultado"
                              inicial={resultado.nome}
                              acao={{
                                parado: 'Salvar resultado',
                                gravando: 'Salvando…',
                                nomeAcessivel: `Salvar o resultado ${resultado.nome}`,
                              }}
                              cancelar={`Cancelar a edição de ${resultado.nome}`}
                              ocupado={ocupado}
                              enviando={
                                renomeacaoDeResultado.isPending && emEdicao === resultado.id
                              }
                              aoEnviar={(nome) => {
                                renomearOResultado(tipo.id, resultado.id, nome)
                              }}
                              aoCancelar={() => {
                                // Cancelar também limpa: uma recusa do servidor continuaria
                                // visível sem nada pendente na tela que a explicasse — e a
                                // confirmação seguinte ficaria muda, porque ela só aparece com
                                // `erro === null`.
                                limparOsAvisos()
                                setEmEdicao(null)
                              }}
                            />
                          ) : (
                            <div className="tipos__linha">
                              <span className="tipos__nome-do-resultado">
                                {resultado.nome}
                                {!podeGerir && resultado.conta_como_exito ? (
                                  <span className="tipos__status"> · {CONTA_COMO_EXITO}</span>
                                ) : null}
                              </span>
                              {podeGerir ? (
                                <label className="tipos__marcador">
                                  <input
                                    type="checkbox"
                                    checked={resultado.conta_como_exito}
                                    disabled={ocupado}
                                    aria-label={`${CONTA_COMO_EXITO}: ${resultado.nome} em ${tipo.nome}`}
                                    onChange={(evento) => {
                                      marcarOExito(tipo.id, resultado.id, evento.target.checked)
                                    }}
                                  />
                                  {CONTA_COMO_EXITO}
                                </label>
                              ) : null}
                              {podeGerir ? (
                                <div className="tipos__acoes">
                                  <button
                                    className="tipos__botao tipos__botao--discreto"
                                    type="button"
                                    disabled={ocupado || formularioAberto}
                                    aria-label={`Renomear ${resultado.nome} em ${tipo.nome}`}
                                    onClick={() => {
                                      limparOsAvisos()
                                      setAConfirmar(null)
                                      setEmEdicao(resultado.id)
                                    }}
                                  >
                                    Renomear
                                  </button>
                                  <AcaoDeExcluir
                                    alvo={`o resultado ${resultado.nome} do tipo ${tipo.nome}`}
                                    acao={{
                                      pedir: 'Remover',
                                      confirmar: 'Confirmar remoção',
                                      substantivo: 'a remoção',
                                    }}
                                    aConfirmar={aConfirmar === resultado.id}
                                    ocupado={ocupado || formularioAberto}
                                    aoPedir={() => {
                                      limparOsAvisos()
                                      setAConfirmar(resultado.id)
                                    }}
                                    aoCancelar={() => {
                                      limparOsAvisos()
                                      setAConfirmar(null)
                                    }}
                                    aoExcluir={() => {
                                      removerOResultado(tipo.id, resultado.id)
                                    }}
                                  />
                                </div>
                              ) : null}
                            </div>
                          )}
                          {podeSugerir ? (
                            <EditorDaSugestao
                              key={`${resultado.tipo_sugerido_id ?? ''}-${String(resultado.prazo_sugerido_em_dias)}`}
                              tipo={tipo}
                              resultado={resultado}
                              tipos={tipos}
                              ocupado={ocupado}
                              aoGravar={(tipoSugeridoId, dias) => {
                                configurarASugestao(tipo.id, resultado.id, tipoSugeridoId, dias)
                              }}
                            />
                          ) : (
                            <p className="tipos__ajuda">{descreverASugestao(resultado, tipos)}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {!podeGerir ? null : comResultadoNovo === tipo.id ? (
                    <FormularioDeNome
                      id={`resultado-novo-${tipo.id}`}
                      rotulo="Nome do resultado"
                      inicial=""
                      acao={{
                        parado: 'Adicionar resultado',
                        gravando: 'Adicionando…',
                        nomeAcessivel: `Adicionar resultado em ${tipo.nome}`,
                      }}
                      cancelar={`Cancelar o novo resultado de ${tipo.nome}`}
                      ocupado={ocupado}
                      enviando={criacaoDeResultado.isPending}
                      aoEnviar={(nome) => {
                        criarOResultado(tipo.id, nome)
                      }}
                      aoCancelar={() => {
                        limparOsAvisos()
                        setComResultadoNovo(null)
                      }}
                    />
                  ) : (
                    <div className="tipos__acoes">
                      <button
                        className="tipos__botao tipos__botao--discreto"
                        type="button"
                        disabled={ocupado || formularioAberto}
                        aria-label={`Novo resultado em ${tipo.nome}`}
                        onClick={() => {
                          limparOsAvisos()
                          setComResultadoNovo(tipo.id)
                        }}
                      >
                        + Novo resultado
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}

          {/*
           * O formulário de criação só existe com o catálogo **conhecido** (`tipos !== undefined`).
           *
           * Sem essa metade ele ficava montado durante o "Carregando…" — e durante a pausa sem
           * rede, que não é erro nem carregamento —, e dava para submeter uma criação contra um
           * catálogo que a tela não leu. O serviço decidiria sozinho: o nome ou nasce, ou volta
           * `409` apontando para um Tipo que quem enviou nunca viu. É a mesma razão pela qual a
           * tela não diz "nenhum tipo cadastrado" antes de saber — afirmar ausência onde só há
           * desconhecimento —, aplicada à escrita em vez de ao texto.
           */}
          {consulta.isError || !podeGerir || tipos === undefined ? null : (
            <div className="tipos__novo">
              <h3 className="tipos__subtitulo">Novo tipo de tarefa</h3>
              <FormularioDeNome
                key={tiposCriados}
                id={ID_DO_CAMPO_DO_NOVO_TIPO}
                rotulo="Nome do tipo"
                inicial=""
                acao={{
                  parado: 'Criar tipo',
                  gravando: 'Criando…',
                  nomeAcessivel: 'Criar tipo de tarefa',
                }}
                ocupado={ocupado}
                enviando={criacaoDeTipo.isPending}
                aoEnviar={criarOTipo}
              />
            </div>
          )}

          {consulta.isError || podeGerir ? null : <p className="tipos__ajuda">{SOMENTE_LEITURA}</p>}

          {erro === null ? null : (
            <p className="tipos__erro" role="alert">
              {erro}
            </p>
          )}
          {/* `role="status"` e não `role="alert"`: deu certo, e o leitor de tela não precisa
              interromper o que a pessoa estiver fazendo para dizer isso. */}
          {confirmacao === null || erro !== null ? null : (
            <p className="tipos__sucesso" role="status">
              {confirmacao}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
